// src/modules/leads/route-planner/RoutePlannerSidebar.js
import React, { useState } from 'react';
import {
  FaMapMarkerAlt, FaSearch, FaTimes, FaRoute, FaFlag,
  FaCrosshairs, FaEdit, FaUndoAlt, FaSearchLocation, FaRedoAlt,
} from 'react-icons/fa';
import GooglePlaceAutocomplete from '../../../components/shared/GooglePlaceAutocomplete';
import MultiSelectDropdown from '../../../components/shared/MultiSelectDropdown';
import LeadSelectionCard from './LeadSelectionCard';
import RouteStopCard from './RouteStopCard';
import FindAlongRoutePanel from './FindAlongRoutePanel';
import RouteSharePanel from './RouteSharePanel';
import {
  PlannerSidebar, SidebarHeader, SidebarScrollArea,
  SectionTitle, SectionSubtitle, PromptCard,
  SearchBox, FilterRow, FilterSelect,
  PrimaryButton, SecondaryButton,
  LeadCountBar, RouteSummaryBar, RouteSummaryStat, Divider,
  EmptyState, Spinner,
} from './routePlannerStyles';
import { MARKER_COLORS } from './routeUtils';
import { StopCard, StopNumber, StopInfo, StopName, StopAddress, ChipButton } from './routePlannerStyles';

const RoutePlannerSidebar = ({ planner, onLeadClick }) => {
  const [showAlongRouteSection, setShowAlongRouteSection] = useState(false);

  const {
    currentStep,
    departure, departureLoading, departureError, setDeparture, useMyLocation,
    destination, effectiveDestination, isRoundTrip, setDestination, setRoundTrip,
    filteredLeads, mappableLeads, selectedLeadIds,
    leadsLoading, availableCities, availableStatuses, availablePipelineStages, availableServices,
    searchQuery, setSearchQuery,
    cityFilter, setCityFilter,
    statusFilter, setStatusFilter,
    pipelineFilter, setPipelineFilter,
    serviceFilter, setServiceFilter,
    dateRange, setDateRange,
    routeStops, routeInfo, isOptimizing, routeError, routeDirty,
    optimizeRoute, toggleLeadInRoute, removeLeadFromRoute,
    canFindAlongRoute, isCalculatingBaseRoute,
    alongRouteDistance, setAlongRouteDistance,
    alongRouteLeads, alongRouteFilters, setAlongRouteFilters,
    showAlongRoute, isSearchingAlongRoute, searchAlongRoute,
    addAlongRouteLeadToRoute,
    googleMapsUrl, copied, copyToClipboard, shareViaWebShare, openInMaps,
    resetRoute, startOver, goToStep, goToShare,
  } = planner;

  const handleLeadClick = (lead) => {
    onLeadClick?.(lead);
  };

  // ─── Step 1: Departure ───────────────────────────────────────────────────
  const renderStep1 = () => (
    <div style={{ padding: '16px' }}>
      <PromptCard>
        <FaMapMarkerAlt className="prompt-icon" />
        <h3>{departure ? 'Starting Point Set' : 'Where are you starting?'}</h3>
        {departure ? (
          <p>{departure.address}</p>
        ) : (
          <>
            {departureError && (
              <p style={{ color: '#e74c3c', fontSize: '0.8rem' }}>{departureError}</p>
            )}
          </>
        )}

        <GooglePlaceAutocomplete
          onSelect={(address, lat, lng) => setDeparture({ lat, lng, address })}
          placeholder="Enter starting address..."
          initialValue={departure?.address || ''}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '14px 0 0' }}>
          <div style={{ flex: 1, height: '1px', background: '#78787844' }} />
          <span style={{ fontSize: '0.75rem', color: '#787878' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: '#78787844' }} />
        </div>

        <SecondaryButton
          onClick={useMyLocation}
          disabled={departureLoading}
          style={{ width: '100%', justifyContent: 'center', marginTop: '14px' }}
        >
          {departureLoading ? (
            <><Spinner style={{ width: 16, height: 16, borderWidth: 2 }} /> Detecting...</>
          ) : (
            <><FaCrosshairs /> Use My Current Location</>
          )}
        </SecondaryButton>
      </PromptCard>
    </div>
  );

  // ─── Step 2: Finish Point ──────────────────────────────────────────────
  const renderStep2 = () => (
    <div style={{ padding: '16px' }}>
      {/* Departure summary */}
      <StopCard style={{ marginBottom: '16px' }}>
        <StopNumber color={MARKER_COLORS.departure}>A</StopNumber>
        <StopInfo>
          <StopName>Start Point</StopName>
          <StopAddress>{departure?.address}</StopAddress>
        </StopInfo>
        <SecondaryButton onClick={() => goToStep(1)} style={{ padding: '4px 8px', fontSize: '0.7rem' }}>
          <FaEdit />
        </SecondaryButton>
      </StopCard>

      <SectionTitle><FaFlag /> Where do you finish?</SectionTitle>
      <SectionSubtitle>Set your final destination — or return to start</SectionSubtitle>

      {/* Round trip toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <ChipButton
          active={isRoundTrip}
          onClick={() => setRoundTrip(true)}
          style={{ flex: 1, textAlign: 'center' }}
        >
          <FaUndoAlt style={{ marginRight: '6px' }} />
          Return to Start
        </ChipButton>
        <ChipButton
          active={!isRoundTrip}
          onClick={() => setRoundTrip(false)}
          style={{ flex: 1, textAlign: 'center' }}
        >
          <FaFlag style={{ marginRight: '6px' }} />
          Different Finish
        </ChipButton>
      </div>

      {!isRoundTrip && (
        <GooglePlaceAutocomplete
          onSelect={(address, lat, lng) => setDestination(address, lat, lng)}
          placeholder="Enter finish address..."
          initialValue={destination?.address || ''}
        />
      )}

      {isRoundTrip && (
        <StopCard style={{ marginTop: '8px' }}>
          <StopNumber color={MARKER_COLORS.destination}>X</StopNumber>
          <StopInfo>
            <StopName>Finish Point (same as start)</StopName>
            <StopAddress>{departure?.address}</StopAddress>
          </StopInfo>
        </StopCard>
      )}
    </div>
  );

  // ─── Step 3: Select Leads ──────────────────────────────────────────────
  const renderStep3 = () => (
    <>
      <SidebarHeader>
        {/* Start + Finish summary */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <StopCard style={{ flex: 1, margin: 0, padding: '8px' }}>
            <StopNumber color={MARKER_COLORS.departure} style={{ width: 22, height: 22, fontSize: '0.65rem' }}>A</StopNumber>
            <StopInfo>
              <StopName style={{ fontSize: '0.75rem' }}>{departure?.address?.split(',')[0]}</StopName>
            </StopInfo>
          </StopCard>
          <StopCard style={{ flex: 1, margin: 0, padding: '8px' }}>
            <StopNumber color={isRoundTrip ? MARKER_COLORS.departure : MARKER_COLORS.destination} style={{ width: 22, height: 22, fontSize: '0.65rem' }}>
              {isRoundTrip ? 'A' : 'X'}
            </StopNumber>
            <StopInfo>
              <StopName style={{ fontSize: '0.75rem' }}>
                {isRoundTrip ? 'Round Trip' : effectiveDestination?.address?.split(',')[0]}
              </StopName>
            </StopInfo>
          </StopCard>
        </div>

        {/* "Find Leads Along Route" — available immediately when start ≠ finish */}
        {canFindAlongRoute && (
          <div style={{ marginBottom: '12px' }}>
            {!showAlongRouteSection ? (
              <PrimaryButton
                onClick={() => setShowAlongRouteSection(true)}
                style={{ background: '#9b59b6' }}
              >
                <FaSearchLocation /> Find Leads Along the Route
              </PrimaryButton>
            ) : (
              <FindAlongRoutePanel
                alongRouteDistance={alongRouteDistance}
                setAlongRouteDistance={setAlongRouteDistance}
                alongRouteLeads={alongRouteLeads}
                alongRouteFilters={alongRouteFilters}
                setAlongRouteFilters={setAlongRouteFilters}
                showAlongRoute={showAlongRoute}
                isSearching={isSearchingAlongRoute}
                onSearch={searchAlongRoute}
                onAddLead={addAlongRouteLeadToRoute}
                onLeadClick={handleLeadClick}
                availableStatuses={availableStatuses}
              />
            )}
          </div>
        )}

        {isCalculatingBaseRoute && !isRoundTrip && (
          <SectionSubtitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Spinner style={{ width: 14, height: 14, borderWidth: 2 }} /> Calculating route...
          </SectionSubtitle>
        )}

        <Divider />

        {/* Manual lead selection filters */}
        <SectionTitle><FaSearch /> Browse & Select Leads</SectionTitle>

        <SearchBox>
          <FaSearch />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <FaTimes style={{ cursor: 'pointer' }} onClick={() => setSearchQuery('')} />
          )}
        </SearchBox>

        <FilterRow>
          <FilterSelect value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="all">All Time</option>
            <option value="7">Last 7 Days</option>
            <option value="14">Last 14 Days</option>
            <option value="21">Last 21 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="60">Last 60 Days</option>
            <option value="90">Last 90 Days</option>
          </FilterSelect>
          <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            {availableStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </FilterSelect>
        </FilterRow>

        <FilterRow>
          <FilterSelect value={pipelineFilter} onChange={(e) => setPipelineFilter(e.target.value)}>
            <option value="all">All Pipeline Stages</option>
            {availablePipelineStages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </FilterSelect>
          <FilterSelect value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
            <option value="all">All Cities</option>
            {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
          </FilterSelect>
        </FilterRow>

        {availableServices.length > 0 && (
          <FilterRow style={{ marginTop: '8px' }}>
            <MultiSelectDropdown
              options={availableServices}
              selected={serviceFilter}
              onChange={setServiceFilter}
              placeholder="All Services"
            />
          </FilterRow>
        )}

        {selectedLeadIds.size > 0 && (
          <PrimaryButton
            onClick={optimizeRoute}
            disabled={isOptimizing || selectedLeadIds.size === 0}
            style={{ marginTop: '8px' }}
          >
            <FaRoute />
            {isOptimizing ? 'Optimizing...' : `Optimize Route (${selectedLeadIds.size} stop${selectedLeadIds.size !== 1 ? 's' : ''})`}
          </PrimaryButton>
        )}

        {routeError && (
          <div style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '8px' }}>{routeError}</div>
        )}
      </SidebarHeader>

      <LeadCountBar>
        {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
        {' | '}{mappableLeads.length} mappable
        {selectedLeadIds.size > 0 && ` | ${selectedLeadIds.size} selected`}
      </LeadCountBar>

      <SidebarScrollArea>
        {leadsLoading ? (
          <EmptyState><Spinner /></EmptyState>
        ) : filteredLeads.length === 0 ? (
          <EmptyState>No leads found. Try adjusting your filters.</EmptyState>
        ) : (
          filteredLeads.map(lead => (
            <LeadSelectionCard
              key={lead.id}
              lead={lead}
              isSelected={selectedLeadIds.has(lead.id)}
              onToggle={toggleLeadInRoute}
              onClick={handleLeadClick}
            />
          ))
        )}
      </SidebarScrollArea>
    </>
  );

  // ─── Step 4: Route Optimized ───────────────────────────────────────────
  const renderStep4 = () => (
    <>
      <SidebarHeader>
        {routeInfo && (
          <RouteSummaryBar>
            <RouteSummaryStat>
              <div className="value">{routeStops.length}</div>
              <div className="label">Stops</div>
            </RouteSummaryStat>
            <RouteSummaryStat>
              <div className="value">{routeInfo.totalDistance}</div>
              <div className="label">Distance</div>
            </RouteSummaryStat>
            <RouteSummaryStat>
              <div className="value">{routeInfo.totalDuration}</div>
              <div className="label">Drive Time</div>
            </RouteSummaryStat>
          </RouteSummaryBar>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <SecondaryButton onClick={() => goToStep(3)} style={{ flex: 1 }}>
            Edit Stops
          </SecondaryButton>
          <PrimaryButton onClick={goToShare} style={{ flex: 1 }} disabled={routeDirty}>
            Share Route
          </PrimaryButton>
        </div>

        {routeDirty && (
          <PrimaryButton
            onClick={optimizeRoute}
            disabled={isOptimizing}
            style={{ marginTop: '8px' }}
          >
            <FaRoute />
            {isOptimizing ? 'Recalculating...' : `Recalculate Route (${selectedLeadIds.size} stops)`}
          </PrimaryButton>
        )}
      </SidebarHeader>

      <SidebarScrollArea>
        {/* Departure */}
        <StopCard>
          <StopNumber color={MARKER_COLORS.departure}>A</StopNumber>
          <StopInfo>
            <StopName>Start</StopName>
            <StopAddress>{departure?.address}</StopAddress>
          </StopInfo>
        </StopCard>

        {/* Ordered route stops */}
        {routeStops.map((lead, index) => (
          <RouteStopCard
            key={lead.id}
            lead={lead}
            index={index}
            legInfo={routeInfo?.legs?.[index]}
            onRemove={removeLeadFromRoute}
          />
        ))}

        {/* Finish */}
        <StopCard>
          <StopNumber color={isRoundTrip ? MARKER_COLORS.departure : MARKER_COLORS.destination}>
            {isRoundTrip ? 'A' : 'X'}
          </StopNumber>
          <StopInfo>
            <StopName>{isRoundTrip ? 'Return to Start' : 'Finish'}</StopName>
            <StopAddress>{effectiveDestination?.address}</StopAddress>
          </StopInfo>
        </StopCard>

        {/* Find Along Route — also available here in step 4 */}
        {canFindAlongRoute && (
          <FindAlongRoutePanel
            alongRouteDistance={alongRouteDistance}
            setAlongRouteDistance={setAlongRouteDistance}
            alongRouteLeads={alongRouteLeads}
            alongRouteFilters={alongRouteFilters}
            setAlongRouteFilters={setAlongRouteFilters}
            showAlongRoute={showAlongRoute}
            isSearching={isSearchingAlongRoute}
            onSearch={searchAlongRoute}
            onAddLead={addAlongRouteLeadToRoute}
            onLeadClick={handleLeadClick}
            availableStatuses={availableStatuses}
          />
        )}
      </SidebarScrollArea>
    </>
  );

  // ─── Step 5: Share ─────────────────────────────────────────────────────
  const renderStep5 = () => (
    <SidebarScrollArea style={{ padding: '16px' }}>
      <RouteSharePanel
        routeInfo={routeInfo}
        routeStops={routeStops}
        googleMapsUrl={googleMapsUrl}
        copied={copied}
        onCopy={copyToClipboard}
        onOpenInMaps={openInMaps}
        onShare={shareViaWebShare}
        onReset={resetRoute}
      />
    </SidebarScrollArea>
  );

  return (
    <PlannerSidebar>
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderStep4()}
      {currentStep === 5 && renderStep5()}

      {currentStep > 1 && (
        <div style={{
          padding: '8px 16px 12px',
          borderTop: `1px solid ${MARKER_COLORS.unselected}33`,
          flexShrink: 0,
        }}>
          <SecondaryButton onClick={startOver} style={{ width: '100%', justifyContent: 'center' }}>
            <FaRedoAlt /> Start Over
          </SecondaryButton>
        </div>
      )}
    </PlannerSidebar>
  );
};

export default RoutePlannerSidebar;
